/* eslint-disable */

import type { Address } from "viem"
import type { Config } from "wagmi"
import { formatUnits } from "viem"
import { readContract } from "@wagmi/core"

import { erc20Abi } from "@/domains/abis/erc20-abi"
import type { ApprovalMode, EmitEvent, TokenAddress, TokenDecimals, TokenSymbol } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { EventEmitter } from "@/domains/library"
import { ManualClPositionCreatedEvent } from "@/domains/thick-v1/events"
import { humanizeTransactionError } from "@/domains/thick-v1/utils/humanize-transaction-error"
import {
  approveOwnerSpenderAllowance,
  createThickNfpPositionPool,
  TransactionReceiptWaitOptions,
  TransactionReturnType,
  waitForAllowance,
} from "@/domains/on-chain"

/**
 * Stable names emitted for every step of the create-position workflow. The
 * composite / store side subscribes to the shared emitter, maps these event
 * names into the stepper UI state, and never has to parse human-readable
 * `title` strings. Kept as a string union so the producer (this file) and
 * the consumer (`CreatePositionStore`) share a single source of truth.
 */
export type CreatePositionStepName =
  | "approve:token0"
  | "approve:token1"
  | "mint"

/**
 * Delay between the approval tx landing and the mint simulation so the RPC
 * node has a chance to pick up the new allowance. The sibling
 * `createLiquidityPoolInteraction` uses the same 2s wait to avoid
 * `safeTransferFrom` failing on fast chains; surfaced as a constant so the
 * unit test can assert the timing without a magic number.
 */
export const ALLOWANCE_PROPAGATION_DELAY_MS = 2000
export const MANUAL_CL_RECEIPT_WAIT_OPTIONS: TransactionReceiptWaitOptions = {
  timeout: 240_000,
  pollingInterval: 2_000,
}

interface CreatePositionInteractionParams {
  account: Address
  chainId: number
  config: Config
  token0: TokenAddress & TokenDecimals & TokenSymbol
  token1: TokenAddress & TokenDecimals & TokenSymbol
  fee: number
  tickLower: bigint
  tickUpper: bigint
  amount0Desired: bigint
  amount1Desired: bigint
  amount0Min: bigint
  amount1Min: bigint
  deadline: number
  nfpManagerAddress: Address
  poolAddress: Address
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
  approvalMode: ApprovalMode
}

export const createPositionInteraction = async ({
  account,
  chainId,
  config,
  token0,
  token1,
  fee,
  tickLower,
  tickUpper,
  amount0Desired,
  amount1Desired,
  amount0Min,
  amount1Min,
  deadline,
  nfpManagerAddress,
  poolAddress,
  emitter,
  eventBus,
  approvalMode,
}: CreatePositionInteractionParams): Promise<TransactionReturnType> => {
  let didApprove0 = false
  let didApprove1 = false

  // Token 0 Approval
  if (amount0Desired > 0n) {
    let currentAllowance0 = 0n
    try {
      const allowanceResult = await readContract(config, {
        address: token0.address,
        abi: erc20Abi,
        functionName: "allowance",
        args: [account, nfpManagerAddress],
        chainId,
      })
      currentAllowance0 = BigInt((allowanceResult as bigint).toString())
    } catch (error) {
      console.error("Error reading token0 allowance:", error)
      // Fail-safe: default to requiring approval if read fails
      currentAllowance0 = 0n
    }

    if (currentAllowance0 < amount0Desired) {
      emitter.emit("approve:token0", {
        title: `Approve ${token0.symbol}`,
        description: `Approving ${formatUnits(amount0Desired, token0.decimals)} ${token0.symbol}`,
        variant: "info",
      })

      const approval0 = await approveOwnerSpenderAllowance({
        account,
        chainId,
        config,
        contractAddress: token0.address,
        spenderAddress: nfpManagerAddress,
        spenderAmount: amount0Desired,
        approvalMode,
        contractRiskLevel: "immutable",
        receiptWait: MANUAL_CL_RECEIPT_WAIT_OPTIONS,
      })

      let approval0Confirmed = approval0.success
      if (approval0.success || approval0.tx) {
        try {
          await waitForAllowance({
            config,
            chainId,
            contractAddress: token0.address,
            ownerAddress: account,
            spenderAddress: nfpManagerAddress,
            requiredAmountWei: amount0Desired,
          })
          approval0Confirmed = true
        } catch (error) {
          if (!approval0.success) {
            console.warn("[ManualClV1.createPosition] approval receipt timed out and allowance recovery failed", {
              tx: approval0.tx,
              error,
            })
          }
        }
      }

      if (approval0Confirmed) {
        didApprove0 = true
        emitter.emit("approve:token0", {
          title: `${token0.symbol} approved`,
          variant: "success",
        })
      }

      if (!approval0Confirmed) {
        emitter.emit("approve:token0", {
          title: `Unable to approve ${token0.symbol}`,
          description: humanizeTransactionError(approval0.error) ?? undefined,
          variant: "error",
          errorMessage: approval0.error,
        })
        return approval0
      }
    }
  }

  // Token 1 Approval
  if (amount1Desired > 0n) {
    let currentAllowance1 = 0n
    try {
      const allowanceResult = await readContract(config, {
        address: token1.address,
        abi: erc20Abi,
        functionName: "allowance",
        args: [account, nfpManagerAddress],
        chainId,
      })
      currentAllowance1 = BigInt((allowanceResult as bigint).toString())
    } catch (error) {
      console.error("Error reading token1 allowance:", error)
      // Fail-safe: default to requiring approval if read fails
      currentAllowance1 = 0n
    }

    if (currentAllowance1 < amount1Desired) {
      emitter.emit("approve:token1", {
        title: `Approve ${token1.symbol}`,
        description: `Approving ${formatUnits(amount1Desired, token1.decimals)} ${token1.symbol}`,
        variant: "info",
      })

      const approval1 = await approveOwnerSpenderAllowance({
        account,
        chainId,
        config,
        contractAddress: token1.address,
        spenderAddress: nfpManagerAddress,
        spenderAmount: amount1Desired,
        approvalMode,
        contractRiskLevel: "immutable",
        receiptWait: MANUAL_CL_RECEIPT_WAIT_OPTIONS,
      })

      let approval1Confirmed = approval1.success
      if (approval1.success || approval1.tx) {
        try {
          await waitForAllowance({
            config,
            chainId,
            contractAddress: token1.address,
            ownerAddress: account,
            spenderAddress: nfpManagerAddress,
            requiredAmountWei: amount1Desired,
          })
          approval1Confirmed = true
        } catch (error) {
          if (!approval1.success) {
            console.warn("[ManualClV1.createPosition] approval receipt timed out and allowance recovery failed", {
              tx: approval1.tx,
              error,
            })
          }
        }
      }

      if (approval1Confirmed) {
        didApprove1 = true
        emitter.emit("approve:token1", {
          title: `${token1.symbol} approved`,
          variant: "success",
        })
      }

      if (!approval1Confirmed) {
        emitter.emit("approve:token1", {
          title: `Unable to approve ${token1.symbol}`,
          description: humanizeTransactionError(approval1.error) ?? undefined,
          variant: "error",
          errorMessage: approval1.error,
        })
        return approval1
      }
    }
  }

  // Allow allowance state to propagate before mint (avoids the mint simulation
  // seeing stale allowance and failing with `safeTransferFrom failed` on fast
  // chains). Matches the behaviour of `createLiquidityPoolInteraction`.
  if (didApprove0 || didApprove1) {
    await new Promise((resolve) => setTimeout(resolve, ALLOWANCE_PROPAGATION_DELAY_MS))
  }

  emitter.emit("mint", {
    title: "Create position",
    description: `Minting position for ${token0.symbol}/${token1.symbol}`,
    variant: "info",
  })

  const result = await createThickNfpPositionPool({
    account,
    chainId,
    config,
    token0: {
      address: token0.address,
    },
    token1: {
      address: token1.address,
    },
    fee: BigInt(fee),
    tickLower,
    tickUpper,
    amount0Desired,
    amount1Desired,
    amount0Min,
    amount1Min,
    deadline,
    nfpManagerAddress,
    receiptWait: MANUAL_CL_RECEIPT_WAIT_OPTIONS,
  })

  if (result.success) {
    // Extract tokenId from result if available
    // The mint function returns: tokenId, liquidity, amount0, amount1
    const tokenId = (() => {
      const outputs = result.simulatedResult as Array<unknown> | undefined
      const maybeTokenId = Array.isArray(outputs) ? outputs[0] : undefined
      return typeof maybeTokenId === "bigint" ? maybeTokenId : undefined
    })()
    const transactionHash = result.tx

    if (transactionHash) {
      eventBus.publish(
        new ManualClPositionCreatedEvent({
          poolAddress,
          tokenId,
          transactionHash,
        }),
      )
    }

    emitter.emit("mint", {
      title: "Position created",
      description: `Position created for ${token0.symbol}/${token1.symbol}`,
      variant: "success",
    })
  }

  if (result.success === false) {
    emitter.emit("mint", {
      title: "Failed to create position",
      description: humanizeTransactionError(result.error) ?? undefined,
      variant: "error",
      errorMessage: result.error,
    })
  }

  return result
}
